import OlVectorImageLayer from 'ol/layer/VectorImage';
import CanvasVectorLayerRenderer from 'ol/renderer/canvas/VectorLayer';
import SimpleGeometry from 'ol/geom/SimpleGeometry';
import GeometryCollection from 'ol/geom/GeometryCollection';

import { LOAD_END, LOAD_START } from 'ui/loading-event-type';

import { FeatureProperty } from 'cadenza/map/feature-property';
import {
  VECTOR_LAYER_SOURCE_FEATURES_LOAD_END,
  VECTOR_LAYER_SOURCE_FEATURES_LOAD_START,
  VectorLayerSource
} from 'cadenza/map/vector-layer/vector-layer-source';
import { LayerProperty } from 'cadenza/map/layer-property';
import { getSelectedFeatureStyle } from 'cadenza/map/vector-layer/selected-feature-style';
import type { Map } from 'cadenza/map/map';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { LoadingStrategyName } from 'cadenza/api-client/dto/layer-dto';

import type { StyleLike } from 'ol/style/Style';
import type { Geometry } from 'ol/geom';
import type Feature from 'ol/Feature';
import type { FrameState } from 'ol/Map';
import type BaseLayer from 'ol/layer/Base';

/**
 * The buffer in pixels around the map viewport extent used by the renderer when getting features from the vector source.
 * So when the user pans or zooms out the map just a little bit, feature reload is not required.
 */
const VECTOR_LAYER_RENDER_BUFFER = 100;

const GEOMETRY_VERTEX_COUNT_THRESHOLD = 10000; // For now,it is just the best guess number. We could do more measurements to find a better threshold.

const LABEL_COUNT_THRESHOLD = 500;

/**
 * At the moment, client-side rendering of labels requires huge effort.
 * When the limit is crossed, then all further labels are skipped / not rendered
 * and a warning is added to SourceProperty.MESSAGES.
 */
const LABEL_COUNT_LIMIT = 2000;

export interface VectorLayerOptions {
  url: string;
  loadingStrategy: LoadingStrategyName;
  opacity: number;
  liveLayer: boolean;
  areTimelinePointsConnectedWithLines?: boolean;
  timelineTimestampAttributeName?: string;
  labelAttributeNames: string[];
}

/**
 * Creates a vector feature layer, which gets the styled features from the server and renders them in the browser
 */
export function createVectorLayer (map: Map, layerId: LayerId, {
  url,
  loadingStrategy,
  opacity,
  liveLayer = false,
  areTimelinePointsConnectedWithLines,
  timelineTimestampAttributeName,
  labelAttributeNames
}: VectorLayerOptions) {
  return new VectorLayer(map, layerId, {
    url,
    loadingStrategy,
    opacity,
    liveLayer,
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames
  });
}

export function isVectorLayer (layer: BaseLayer) {
  return layer instanceof OlVectorImageLayer;
}

/**
 * Custom vector layer extends ol/layer/VectorImage, so by default it renders the features with CanvasVectorImageLayerRenderer,
 * but switches dynamically to CanvasVectorLayerRenderer in case geometry vertex count is not big
 * and it is ok in terms of performance to render geometries instantly also during zoom animation.

 * - CanvasVectorLayerRenderer      - is used in ol/layer/Vector, renders the features instantly,
 *                                    also during zoom animations, looks better, but requires more resources.
 * - CanvasVectorImageLayerRenderer - is used in ol/layer/VectorImage, renders the features to an image and reuse this image
 *                                    during map view animations, is meant for more performant rendering.
 *                                    So it is not redrawn during zoom, but only after zooming is finished.
 *
 * Each time the features are loaded, we check if total vertex and labeled features amount does not exceed the limits:
 *   - if limit is not reached, features are rendered with a fancy CanvasVectorLayerRenderer;
 *   - if limit is reached, features are rendered with a more performant CanvasVectorImageLayerRenderer.
 */
class VectorLayer extends OlVectorImageLayer<VectorLayerSource> {

  #isRenderedAsImage = false;
  #instantRenderer?: CanvasVectorLayerRenderer;

  constructor (map: Map, layerId: LayerId, {
    url,
    loadingStrategy,
    opacity,
    liveLayer = false,
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames
  }: VectorLayerOptions) {
    const vectorLayerSource = new VectorLayerSource(map, layerId, {
      url,
      loadingStrategy,
      layerRenderBuffer: VECTOR_LAYER_RENDER_BUFFER,
      areTimelinePointsConnectedWithLines,
      timelineTimestampAttributeName,
      labelAttributeNames,
      labelCountLimit: LABEL_COUNT_LIMIT
    });
    super({
      source: vectorLayerSource,
      opacity,
      renderBuffer: VECTOR_LAYER_RENDER_BUFFER,
      properties: {
        [LayerProperty.ID]: layerId,
        liveLayer
      },
      // @ts-ignore something is wrong with OL types, render is part of Options in ol/layer/Layer, which is one of the ol/layer/VectorImage base classes
      render: (frameState: FrameState, target: HTMLElement) => this.renderWithInstantOrImageRenderer(frameState, target)
    });
    this.#instantRenderer = new CanvasVectorLayerRenderer(this);

    this.on(`change:${LayerProperty.SELECTED_FEATURE_IDS}` as never, () => updateSelectedFeatureStyles(this));
    vectorLayerSource.on(VECTOR_LAYER_SOURCE_FEATURES_LOAD_END, () => {
      this.#isRenderedAsImage = shouldBeRenderedAsImageLayer(vectorLayerSource.getFeatures());
      updateSelectedFeatureStyles(this);
      // map target is eventbus in here so we can use it to communicate with loading cover
      map?.getTargetElement().dispatchEvent(new CustomEvent(LOAD_END));
    });
    vectorLayerSource.on(VECTOR_LAYER_SOURCE_FEATURES_LOAD_START, () => {
      // map target is eventbus in here so we can use it to communicate with loading cover
      map?.getTargetElement().dispatchEvent(new CustomEvent(LOAD_START));
    });
  }

  renderWithInstantOrImageRenderer (frameState: FrameState, target: HTMLElement) {
    const layerRenderer = this.#isRenderedAsImage ? this.getRenderer() : this.#instantRenderer;
    if (layerRenderer && layerRenderer.prepareFrame(frameState)) {
      this.rendered = true;
      return layerRenderer.renderFrame(frameState, target);
    }
    return null;
  }

  override disposeInternal () {
    if (this.#instantRenderer) {
      this.#instantRenderer.dispose();
      this.#instantRenderer = undefined;
    }
    super.disposeInternal();
  }

}

// TODO_LATER (solovjova, 12.05.2020, CADENZA-15086 Routing-VectorLayer): selection (see https://conf.disy.net/x/eYepG)
// This is a minimal working solution to enable feature selection visualization for the vector
// layers rendered on the client side. Things to improve in the future:
// - Selected feature styling.
// - Logic simplification.
//    Now the selection relies on the same selection mechanism used for the WMS layers.
//    The selection request with a clicked point or a drawn geometry is send to the server,
//    the server finds the hit features, updates the selection state and return selected features to the client.
//    For the vector layers it could be simplified, the features are available on the client,
//    so there is no need to do the server request. Only do the request to update the state on the server.
function updateSelectedFeatureStyles (layer: VectorLayer) {
  const featureIds = layer.get(LayerProperty.SELECTED_FEATURE_IDS);
  layer.getSource()?.getFeatures()
    .forEach(feature => {
      const isSelected = featureIds.includes(String(feature.getId()));
      if (isSelected && !feature.get(FeatureProperty.ORIGINAL_STYLE)) {
        feature.set(FeatureProperty.ORIGINAL_STYLE, feature.getStyle());
        feature.setStyle(getSelectedFeatureStyle(feature));
      } else if (!isSelected && feature.get(FeatureProperty.ORIGINAL_STYLE)) {
        feature.setStyle(feature.get(FeatureProperty.ORIGINAL_STYLE));
        feature.unset(FeatureProperty.ORIGINAL_STYLE);
      }
    });
}
function shouldBeRenderedAsImageLayer (features: Feature[]) {
  const totalVertexCount = features.flatMap(feature => coordinateCount(feature.getGeometry())).reduce((sum, count) => sum + count, 0);
  if (totalVertexCount > GEOMETRY_VERTEX_COUNT_THRESHOLD) {
    return true;
  }
  const totalLabelCount = features.reduce((count, feature) => feature.get(FeatureProperty.IS_LABELED) ? count + 1 : count, 0);
  if (totalLabelCount > LABEL_COUNT_THRESHOLD) {
    return true;
  }
  return features.some(feature => isStyleProblematicForVectorLayer(feature.getStyle()));
}

function coordinateCount (geometry?: Geometry): number {
  if (geometry instanceof SimpleGeometry) {
    return geometry.getFlatCoordinates().length / geometry.getStride();
  } else if (geometry instanceof GeometryCollection) {
    return geometry.getGeometries().reduce((vertexCount: number, geometryPart: Geometry) => vertexCount + coordinateCount(geometryPart), 0);
  }
  return 0;
}

function isStyleProblematicForVectorLayer (style?: StyleLike) {
  if (typeof style === 'function') {
    return false;
  }
  const styles = Array.isArray(style) ? style : [ style ];
  return styles.some(s => s?.getStroke()?.getLineDash()); // Line dashes are causing an unpleasant visual effect during zoom animation as the dash pattern starting point is constantly changing.
}
